import {
  AbstractO2dElement,
  html,
  customElement,
  property,
  state,
  INJECTED_BUTTON_CLICK_EVENT,
  type InjectedButton,
  type InjectedButtonClickEventDetail,
  type TemplateResult,
} from '@o2d/shared';
import { nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { repeat } from 'lit/directives/repeat.js';
import { takeUntil } from 'rxjs';
import { type Tool } from '../core/tool/tool.types.js';
import type { O2dWhiteboardElement } from './o2d-whiteboard.element.js';

interface ToolDef {
  readonly id: Tool;
  readonly label: string;
  readonly title: string;
}

/**
 * All tools except smiley, which has its own dropdown button.
 *
 * The icon is no longer rendered inline here — each button carries a unique
 * `tool-<id>` class, and the matching Font-Awesome glyph is attached via
 * `button.tool-<id>::before { content: "\fXXX" }` in `whiteboard.css`
 * (horstplan `buttonform-marker` button style). `whiteboard.css` is therefore
 * the single source of truth for which icon each tool shows.
 */
const TOOLS: ReadonlyArray<ToolDef> = [
  { id: 'select',    label: 'Auswahl',   title: 'Auswahl (Standard)' },
  { id: 'text',      label: 'Text',      title: 'Textfeld anlegen'   },
  { id: 'sticky',    label: 'Notiz',     title: 'Haftnotiz anlegen'  },
  { id: 'line',      label: 'Linie',     title: 'Linie zeichnen'     },
  { id: 'rectangle', label: 'Rechteck',  title: 'Rechteck einfügen'  },
  { id: 'ellipse',   label: 'Ellipse',   title: 'Ellipse einfügen'   },
  { id: 'checkbox',  label: 'Checkbox',  title: 'Checkbox einfügen'  },
  { id: 'person',    label: 'Person',    title: 'Person einfügen'    },
];

/**
 * `<o2d-whiteboard-toolbar>` — the top-of-board tool picker.
 *
 * The smiley button is rendered separately with a dropdown: clicking it opens a
 * grid of emoji choices.  Selecting one sets the active emoji, activates the
 * smiley tool and closes the dropdown.  The user then drags on the canvas to
 * place the emoji at the desired size (same drag-to-create pattern as rectangle
 * and ellipse).
 */
@customElement('o2d-whiteboard-toolbar')
export class O2dWhiteboardToolbarElement extends AbstractO2dElement {
  /**
   * Reference to the whiteboard this toolbar drives.
   * @deprecated PRD-H: The wrapper `<o2d-whiteboard>` now renders the toolbar
   * internally and injects this reference automatically. Hosting the toolbar
   * standalone and assigning `board` manually is retained for tests/back-compat
   * only and will be removed in a future release.
   */
  @property({ attribute: false }) board?: O2dWhiteboardElement;

  /**
   * Host-supplied buttons appended to the toolbar — right of the internal tools
   * but left of the `margin-left:auto` zoom cluster, so they group visually with
   * the tools (O2D.P-PRD-021 §TR5). They never replace an internal tool.
   * Clicking one emits `injected-button-click` carrying the descriptor `id`.
   *
   * O2D.P-PRD-021 §TR7 reactivity contract: assign a NEW array to trigger a
   * re-render. Lit compares this property by identity, so mutating the existing
   * array in place (`push`, index assignment, changing a descriptor field) does
   * NOT re-render the toolbar.
   */
  @property({ attribute: false }) injectedButtons: InjectedButton[] = [];

  /**
   * Host-supplied buttons rendered BEFORE the internal tools, i.e. at the very
   * left of the toolbar (Anke, 2026-08-17 — e.g. a Save button placed right
   * next to the Select tool instead of grouped with the right-hand injected
   * buttons). Same reactivity contract as `injectedButtons` above: assign a
   * NEW array to re-render, never mutate in place.
   */
  @property({ attribute: false }) injectedButtonsStart: InjectedButton[] = [];

  /**
   * Anke, 2026-09-15: edit-lock — while `true`, the drawing TOOLS group
   * (Auswahl/Text/Notiz/Linie/Rechteck/Ellipse/Checkbox/Person/Icon) is not
   * rendered at all, mirroring `text/document mode`'s existing
   * "Bearbeiten"-gated toolbar. Mirrors `O2dWhiteboardElement.readonly`
   * (PRD-X §TR4), which the wrapper passes straight through — that property
   * already locks the tool CONTROLLER and blocks interaction/edit, but does
   * not by itself hide these buttons; this property is the toolbar's own
   * half of the same lock. Host-injected buttons (`injectedButtons`/
   * `injectedButtonsStart` — e.g. the host's own Edit/Save button, the
   * filter toggle) and the always-available grid/zoom/fit/export cluster
   * stay visible regardless, since they are either how the User gets INTO
   * edit mode, or plain view/navigation actions with nothing to lock.
   */
  @property({ type: Boolean, reflect: true }) readonly = false;

  @state() private activeTool: Tool = 'select';
  /** Current viewport scale, kept in sync via `viewport-changed`. */
  @state() private _zoom = 1;
  /** Spiegel von `board.gridEnabled` fuer den gedrueckten Zustand der Taste.
   *  Wird beim Umschalten gesetzt; die Taste ist die einzige Stelle, die das
   *  Raster schaltet, deshalb genuegt der lokale Spiegel ohne eigenes Ereignis. */
  @state() private _grid = false;

  /** Unsubscribe callbacks for board-state listeners and bus subscriptions. */
  private boardTeardown: Array<() => void> = [];

  private refreshZoom = (): void => {
    this._zoom = this.board?.viewport.scale ?? 1;
  };

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('board')) {
      this.unsubscribeBoard();
      if (this.board) this.subscribeBoard(this.board);
    }
  }

  /** Tears down all board-driven subscriptions (idempotent). */
  private unsubscribeBoard(): void {
    for (const off of this.boardTeardown.splice(0)) off();
  }

  /** Wires up bus + boardState subscriptions for the given board. */
  private subscribeBoard(board: O2dWhiteboardElement): void {
    this.activeTool = board.tool;
    // Subscribe to tool$ stream via the bus (PRD-G T14, KISS C3 — no controller).
    if (board.bus) {
      const bus = board.bus;
      const toolSub = bus.tool$
        .pipe(takeUntil(bus.destroy$))
        .subscribe((t) => {
          this.activeTool = t;
        });
      this.boardTeardown.push(() => toolSub.unsubscribe());
    }
    // Keep the zoom readout in sync with pan/zoom.
    this.boardTeardown.push(
      board.boardState.on('viewport-changed', this.refreshZoom),
    );
    this.refreshZoom();
    this._grid = board.gridEnabled;
    // Review 0023, Lauf 02: eine Quelle der Wahrheit. Vorher hatte die
    // Werkzeugleiste zwei voneinander unabhängige Eingänge für dieselbe Frage
    // — `board` und `readonly` —, und `subscribeBoard()` las `board.readonly`
    // nie. In der eingebetteten Nutzung fiel das nicht auf, weil der Wrapper
    // beides aus demselben Wert setzt; in der im README dokumentierten
    // Standalone-Nutzung (`toolbar.board = board`) blieb die Werkzeuggruppe
    // auf einem gesperrten Board sichtbar und behauptete Bearbeitbarkeit.
    //
    // Wie `_grid` darüber ist das ein Abgleich zum Zeitpunkt des Abonnierens:
    // Das Board meldet keine `readonly`-Wechsel über den Bus. Für die
    // eingebettete Nutzung spielt das keine Rolle — dort hält das Binding des
    // Wrappers die Property ohnehin laufend nach.
    this.readonly = board.readonly;
  }

  /**
   * Forces re-subscription to the current `board.bus` (W1 PRD-H1).
   *
   * Called by `<o2d-whiteboard>`'s `#injectBusIntoChildren()` after a
   * disconnect+reconnect cycle. Lit's `updated('board')` hook does NOT fire
   * when the wrapper reassigns the same board reference, so we cannot rely
   * on the property-change path to re-wire to the NEW bus instance.
   *
   * Idempotent: safe to call multiple times — `unsubscribeBoard()` tears the
   * subscription list down before `subscribeBoard()` rebuilds it.
   */
  public refreshBusBindings(): void {
    this.unsubscribeBoard();
    if (this.board) this.subscribeBoard(this.board);
  }

  override disconnectedCallback(): void {
    this.unsubscribeBoard();
    super.disconnectedCallback();
  }

  // ---- Zoom display -------------------------------------------------------

  private onZoomResetClick(e: MouseEvent): void {
    (e.currentTarget as HTMLElement).blur();
    this.board?.resetZoom();
  }

  /** Zoom & pan so the whole board content fits into view ("auf alles zoomen"). */
  private onZoomFitClick(e: MouseEvent): void {
    (e.currentTarget as HTMLElement).blur();
    this.board?.fitToContent();
  }

  // Anke, 2026-09-03: die Funktion hinter "In 100% konvertieren" war nicht
  // richtig durchdacht — Button und Handler entfernt (`board.normaliseZoom()`
  // bleibt unverändert bestehen, falls die Taste später überarbeitet
  // zurückkommt; siehe Git-Historie für den ursprünglichen Handler/Button).

  /** Ganzes Board als Vektorgrafik (SVG) herunterladen (Anke, 2026-07-16). */
  private onExportSvgClick(e: MouseEvent): void {
    (e.currentTarget as HTMLElement).blur();
    this.board?.exportSvg();
  }

  /** Nur die ausgewaehlten Objekte als Vektorgrafik (SVG) herunterladen. */
  private onExportSelectionSvgClick(e: MouseEvent): void {
    (e.currentTarget as HTMLElement).blur();
    this.board?.exportSelectionSvg();
  }

  /**
   * Raster ein/aus (Anke, 2026-07-16). Solange es an ist, rasten neue,
   * verschobene und skalierte Objekte am Raster ein.
   */
  private onGridToggleClick(e: MouseEvent): void {
    (e.currentTarget as HTMLElement).blur();
    this.board?.toggleGrid();
    this._grid = this.board?.gridEnabled ?? false;
  }

  // ---- Regular tool button ------------------------------------------------

  /**
   * Dispatch a `tool/set` UiAction onto the bus (TR12). The board's
   * `ToolController` subscribes to this event and applies the tool change.
   */
  private dispatchToolChange(tool: Tool): void {
    this.board?.bus?.uiActions$.next({ type: 'tool/set', tool });
  }

  private onButtonClick(tool: Tool, e: MouseEvent): void {
    this.dispatchToolChange(tool);
    (e.currentTarget as HTMLElement).blur();
  }

  // ---- Injected buttons -----------------------------------------------

  /**
   * Renders host-injected buttons (e.g. horstplan's "Speichern") in the same
   * structural shape as the built-in TOOLS buttons above, instead of
   * `@o2d/shared`'s generic `renderInjectedButtons()` (compact "object-button"
   * shape used by other hosts like `o2d-dialog`) — this toolbar is the sole
   * `buttonform-marker` adopter (see `verify-button-css-contract.mjs`), so its
   * own injected buttons should look and behave exactly like its own tool
   * buttons: no inline `<i>` icon, the icon comes from a `button.<id>::before`
   * glyph the host defines itself (mirrors `tool-<id>::before` in
   * `whiteboard.css`), and the label is `data-label` for a `::after` rule.
   *
   * Grouping: every button gets its own `div.button-group`, EXCEPT that
   * consecutive buttons sharing an `InjectedButton.group` key are wrapped by
   * ONE div (e.g. Bearbeiten/Speichern + Abbrechen side by side in one group).
   */
  private renderInjectedToolButtons(buttons: InjectedButton[]): TemplateResult {
    const groups: InjectedButton[][] = [];
    for (const button of buttons) {
      const last = groups[groups.length - 1];
      if (button.group !== undefined && last !== undefined && last[0]!.group === button.group) {
        last.push(button);
      } else {
        groups.push([button]);
      }
    }

    return html`${repeat(
      groups,
      // Key = the id of the group's FIRST button. That id is unique (ids of
      // injected buttons are required to be — the inner `repeat` below keys
      // on them too) and it is tied to the button itself, not to where the
      // group happens to sit, so it stays the same when a group is added or
      // removed further left. A position-based key would change for every
      // group behind the edit, making `repeat` throw those parts away and
      // rebuild the buttons (lost focus, aborted transitions).
      // NOT the group name (`group[0]!.group ?? group[0]!.id`, the original
      // expression): `InjectedButton.group` expressly allows the same name at
      // two separated places, which yields two groups — and so twice the
      // same key. `repeat`'s key->part map then keeps only the last one, and
      // on a re-paint it reuses the wrong part and a whole group can drop out
      // of the DOM.
      (group) => group[0]!.id,
      (group) => html`
        <div class="button-group">
          ${repeat(
            group,
            (button) => button.id,
            (button) => html`
              <button
                type="button"
                class="toolbar-button ${button.id} buttonform-marker ${button.variant ?? 'tertiary'}"
                data-tool=${button.id}
                data-label=${button.label}
                title=${ifDefined(button.title)}
                aria-label=${button.label}
                ?disabled=${button.disabled ?? false}
                @click=${() =>
                  this.fireEvent<InjectedButtonClickEventDetail>(INJECTED_BUTTON_CLICK_EVENT, {
                    id: button.id
                  })}
              ><div class="hover-effect"></div></button>
            `
          )}
        </div>
      `
    )}`;
  }

  // ---- Render -------------------------------------------------------------

  protected override render(): TemplateResult {
    const isSmileyActive = this.activeTool === 'smiley';

    return html`
      <div class="toolbar-inner" part="inner" role="toolbar" aria-label="Whiteboard-Werkzeuge">
        <!-- Host-injected buttons rendered BEFORE the tools, at the toolbar's
             left edge (Anke, 2026-08-17). Stefan, 2026-08-30: the wrapping
             span.toolbar-injected-start was removed then (buttons rendered
             directly into the flex row). Anke, 2026-09-03: each injected
             button now gets its own div.button-group wrapper again (see
             renderInjectedToolButtons() below) — one group per button,
             unlike the single shared group further down for the TOOLS/Icon
             cluster. -->
        ${this.renderInjectedToolButtons(this.injectedButtonsStart)}
        ${this.readonly ? nothing : html`
        <div class="button-group">
          ${TOOLS.map((t) => html`
            <button
              type="button"
              class="toolbar-button tool-${t.id} buttonform-marker tertiary ${t.id === this.activeTool ? 'active' : ''}"
              data-tool=${t.id}
              data-label=${t.label}
              title=${t.title}
              aria-label=${t.label}
              aria-pressed=${t.id === this.activeTool ? 'true' : 'false'}
              @click=${(e: MouseEvent) => this.onButtonClick(t.id, e)}
            ><div class="hover-effect"></div></button>
          `)}

          <!-- Smiley tool button (motif is chosen from the options circle after placing) -->
          <button
            type="button"
            class="toolbar-button toolbar-button--smiley tool-smiley buttonform-marker tertiary ${isSmileyActive ? 'active' : ''}"
            data-tool="smiley"
            data-label="Icon"
            title="Icon einfügen"
            aria-label="Icon einfügen"
            aria-pressed=${isSmileyActive ? 'true' : 'false'}
            @click=${(e: MouseEvent) => this.onButtonClick('smiley', e)}
          ><div class="hover-effect"></div></button>
        </div>
        `}

        <!-- Host-injected buttons (TD-020 Phase 1a) — appended after the internal
             tools and BEFORE the margin-left:auto zoom cluster, so they group
             visually with the tools on the left. -->
        ${this.renderInjectedToolButtons(this.injectedButtons)}

        <!-- Undo / Redo live in the SVG options circle (action-undo / action-redo). -->

        <!-- Rechter Cluster (Anke, 2026-07-26): Raster / Normieren / Einpassen /
             100 % / Export / Ausschnitt. Die erste Taste traegt margin-left:auto
             (CSS --grid) und schiebt die ganze Gruppe an den rechten Rand. -->

        <!-- Raster ein/aus. Gedrueckt = an; solange an, rasten Objekte ein. -->
        <button
          type="button"
          class="toolbar-button toolbar-button--grid tool-grid buttonform-marker tertiary ${this._grid ? 'active' : ''}"
          data-label=${'An raster\nausrichten'}
          title="Raster ein/aus (Objekte rasten am Raster ein)"
          aria-label="Raster ein/aus"
          aria-pressed=${this._grid ? 'true' : 'false'}
          @click=${this.onGridToggleClick}
        ><div class="hover-effect"></div></button>

        <!-- "Auf 100 % normalisieren" (tool-normalise, "In 100% konvertieren")
             — Anke, 2026-09-03: Funktion war nicht richtig durchdacht, Button
             entfernt (siehe Git-Historie dieser Zeile für die Original-Markup
             und den Handler, falls die Taste überarbeitet zurückkommen soll). -->

        <!-- "Auf alles zoomen" -->
        <button
          type="button"
          class="toolbar-button toolbar-button--fit tool-fit buttonform-marker tertiary"
          data-label=${'Auf alles\nzoomen'}
          title="Auf alles zoomen"
          aria-label="Auf alles zoomen"
          @click=${this.onZoomFitClick}
        ><div class="hover-effect"></div></button>

        <!-- Zoom-Anzeige / auf 100 % zuruecksetzen. Wie die anderen Tasten
             (buttonform-marker) mit Lupen-Icon; der Prozentwert steht als
             data-label in der Textzeile. -->
        <button
          type="button"
          class="toolbar-button toolbar-button--zoom tool-zoom buttonform-marker tertiary"
          data-label=${`Ansicht\n${Math.round(this._zoom * 100)}%`}
          title="Zoom zurücksetzen (100%)"
          aria-label="Zoom zurücksetzen"
          @click=${this.onZoomResetClick}
        ><div class="hover-effect"></div></button>

        <!-- Export als Vektorgrafik: ganzes Board / nur die Auswahl -->
        <button
          type="button"
          class="toolbar-button toolbar-button--export tool-export buttonform-marker tertiary"
          data-label=${'Alles\nexportieren'}
          title="Ganzes Board als Vektorgrafik (SVG) herunterladen"
          aria-label="Board als SVG exportieren"
          @click=${this.onExportSvgClick}
        ><div class="hover-effect"></div></button>

        <button
          type="button"
          class="toolbar-button toolbar-button--export-selection tool-export-selection buttonform-marker tertiary"
          data-label=${'Auswahl\nexportieren'}
          title="Nur die Auswahl als Vektorgrafik (SVG) herunterladen"
          aria-label="Auswahl als SVG exportieren"
          @click=${this.onExportSelectionSvgClick}
        ><div class="hover-effect"></div></button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'o2d-whiteboard-toolbar': O2dWhiteboardToolbarElement;
  }
}
